import { useState } from 'react';
import { Phone, Mail, Calendar, FileText, Plus, Search } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

type CommunicationType = 'call' | 'email' | 'meeting' | 'note';

interface Communication {
  id: string;
  type: CommunicationType;
  clientName: string;
  subject: string;
  content: string;
  createdBy: string;
  createdAt: Date;
}

const MOCK_COMMUNICATIONS: Communication[] = [
  {
    id: '1',
    type: 'call',
    clientName: 'First National Bank',
    subject: 'Quarterly Review Call',
    content: 'Discussed Q4 performance and upcoming feature rollout.',
    createdBy: 'John Doe',
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
  },
  {
    id: '2',
    type: 'email',
    clientName: 'Community Credit Union',
    subject: 'Invoice Follow-up',
    content: 'Sent reminder for outstanding invoice INV-2024-001.',
    createdBy: 'John Doe',
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000),
  },
  {
    id: '3',
    type: 'meeting',
    clientName: 'Metro Financial Services',
    subject: 'Onboarding Kickoff',
    content: 'Initial onboarding meeting to discuss requirements and timeline.',
    createdBy: 'Jane Smith',
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
  },
  {
    id: '4',
    type: 'note',
    clientName: 'Summit Bank & Trust',
    subject: 'Internal Note',
    content: 'Client expressed interest in expanding BLAST features.',
    createdBy: 'John Doe',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
  },
];

const TYPE_ICONS: Record<CommunicationType, typeof Phone> = {
  call: Phone,
  email: Mail,
  meeting: Calendar,
  note: FileText,
};

const TYPE_LABELS: Record<CommunicationType, string> = {
  call: 'Phone Call',
  email: 'Email',
  meeting: 'Meeting',
  note: 'Note',
};

export function Communications() {
  const { canWrite } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<CommunicationType | 'all'>('all');

  let filtered = MOCK_COMMUNICATIONS;
  
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter(
      (c) =>
        c.clientName.toLowerCase().includes(query) ||
        c.subject.toLowerCase().includes(query) ||
        c.content.toLowerCase().includes(query)
    );
  }

  if (typeFilter !== 'all') {
    filtered = filtered.filter((c) => c.type === typeFilter);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate dark:text-mist">
            Communications
          </h1>
          <p className="text-chalkboard dark:text-rain-cloud mt-1">
            Track all client interactions
          </p>
        </div>
        {canWrite('communications') && (
          <button className="btn-primary">
            <Plus className="w-4 h-4" />
            Log Communication
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-french-gray" />
              <input
                type="text"
                placeholder="Search communications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as CommunicationType | 'all')}
              className="select w-full sm:w-48"
            >
              <option value="all">All Types</option>
              <option value="call">Phone Calls</option>
              <option value="email">Emails</option>
              <option value="meeting">Meetings</option>
              <option value="note">Notes</option>
            </select>
          </div>
        </div>
      </div>

      {/* Communications List */}
      <div className="space-y-4">
        {filtered.map((comm) => {
          const Icon = TYPE_ICONS[comm.type];
          return (
            <div key={comm.id} className="card">
              <div className="card-body">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-pond/10 rounded-lg">
                    <Icon className="w-5 h-5 text-pond dark:text-pond-light" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-slate dark:text-mist">
                          {comm.subject}
                        </h3>
                        <p className="text-sm text-pond dark:text-pond-light">
                          {comm.clientName}
                        </p>
                      </div>
                      <span className="badge-neutral">{TYPE_LABELS[comm.type]}</span>
                    </div>
                    <p className="text-sm text-chalkboard dark:text-rain-cloud mt-2">
                      {comm.content}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-french-gray">
                      <span>By {comm.createdBy}</span>
                      <span>•</span>
                      <span>{formatRelativeTime(comm.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="card">
            <div className="card-body text-center py-8">
              <p className="text-chalkboard dark:text-rain-cloud">No communications found.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
